// import Vue from 'vue';
import { createWebHistory, createRouter } from 'vue-router';
import store from '@store';
import Permissions from '@helpers/permissions';
import Roles from '@helpers/roles';
import RFIDScan from '@views/RFIDScan.vue';


const ifNotAuthenticated = (to, from, next) => {
    if (!store.getters['auth/getIsAuthenticated']) {
        next();
        return;
    }
    next('/'); // Redirect authenticated users away from auth routes
};

const ifAuthenticated = (to, from, next) => {
    if (store.getters['auth/getIsAuthenticated']) {
        next();
        return;
    }
    next('/rfid-scan'); 
};

function $routerCan(action, permission) {
    const permissions = store.getters['auth/getPermissions'];
    const hasPermission = permissions[permission] ? permissions[permission].indexOf(action) > -1 : false;
    return hasPermission;
}

const routes = [
    {
        path: '/',
        name: 'home',
        component: () => import('@views/RoomSchedule.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/admin',
        name: 'admin',
        component: () => import('@admin/Admin.vue'),
        beforeEnter: (to, from, next) => {
            if (Roles.isAdmin()) {
                if (store.getters['auth/getIsAuthenticated']) {
                    next();
                } else {
                    next({ name: 'SignIn' });
                }
            } else {
                next('/');
            }
        },
        children: [
            {
                path: '',
                component: () => import('@admin/Users.vue'),
                name: '/admin/users',
            },
            {
                path: 'users',
                component: () => import('@admin/Users.vue'),
                name: 'users',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadUser()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'users/create',
                component: () => import('@admin/CreateUser.vue'),
                name: 'users.create',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadUser()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'users/:id/edit',
                component: () => import('@admin/EditUser.vue'),
                name: 'users.edit',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadUser()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'group-management',
                component: () => import('@admin/GroupManagement.vue'),
                name: 'group-management',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadUserGroup()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'group-management/:id/edit',
                component: () => import('@admin/EditGroupManagement.vue'),
                name: 'group-management.edit',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadUserGroup()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'subjects-management',
                component: () => import('@admin/SubjectsManagement.vue'),
                name: 'subjects-management',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadSubject()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
            {
                path: 'room-groups',
                component: () => import('@admin/RoomGroups.vue'),
                name: 'room-groups',
                beforeEnter: (to, from, next) => {
                    if (Permissions.isReadRoomGroup()) {
                        if (store.getters['auth/getIsAuthenticated']) {
                            next();
                        } else {
                            next({ name: 'SignIn' });
                        }
                    } else {
                        next('/');
                    }
                },
            },
        ],
    },
    {
        path: '/individual-plan',
        name: 'individual-plan',
        component: () => import('@views/IndividualPlan.vue'),
        beforeEnter: (to, from, next) => {
            try {
                if ($routerCan('r', 'individual_plan') || $routerCan('rs', 'individual_plan')) {
                    ifAuthenticated(to, from, next);
                    return;
                }
                next('/');
            } catch (error) {
                console.error('Error in Individual Plan route guard:', error);
                next('/');
            }
        },
    },
    {
        path: '/sign-in',
        name: 'SignIn',
        component: () => import('@views/Auth/SignIn.vue'),
        beforeEnter: ifNotAuthenticated,
        meta: { hideHeader: true }
    },
    {
        path: '/sign-in-e',
        name: 'SignInE',
        component: () => import('@views/Auth/SignInEclass.vue'),
        beforeEnter: ifNotAuthenticated,
        meta: { hideHeader: true }
    },
    {
        path: '/sign-up',
        name: 'SignUp',
        component: () => import('@views/Auth/SignUp.vue'),
        beforeEnter: ifNotAuthenticated,
        meta: { hideHeader: true }
    },
    {
        path: '/forgot-password',
        name: 'forgotPassword',
        component: () => import('@views/Auth/ForgotPassword.vue'),
        beforeEnter: ifNotAuthenticated,
        meta: { hideHeader: true }
    },
    {
        path: '/reset-password',
        name: 'resetPassword',
        component: () => import('@views/Auth/ResetPassword.vue'),
        beforeEnter: ifNotAuthenticated,
        meta: { hideHeader: true }
    },
    {
        path: '/rfid-scan',
        name: 'RFIDScan',
        component: RFIDScan,
    },
    {
        path: '/:catchAll(.*)',
        redirect: '/'
    }
]
const router = createRouter({
    history: createWebHistory(),
    routes, //same --- > routes:routes
})

router.beforeEach(async (to, from, next) => {
    const isAuthenticated = store.getters['auth/getIsAuthenticated'];
    const isRfidLogin = store.getters['auth/getIsRfidLogin'];

    // Special handling for session timeout
    const isSessionTimeout = from.name === 'individual-plan' && !isAuthenticated;
    if (isSessionTimeout) {
        console.warn("Session timed out while on Individual Plan page");
        return next({ name: 'RFIDScan' });
    }

    let user = store.getters['auth/getUser']?.data;

    if (isAuthenticated && !user?.id) {
        try {
            await store.dispatch('auth/auth');
            user = store.getters['auth/getUser']?.data;
            
            // Ensure permissions are loaded
            if (to.path === '/individual-plan') {
                try {
                    await store.dispatch('auth/permissions');
                } catch (error) {
                    console.error('Error loading permissions:', error);
                }
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
            await store.dispatch('auth/logout');
            return next({ name: 'SignIn' });
        }
    }

    if (isAuthenticated && user?.id) {
        // Always ensure permissions are loaded for authenticated users
        const currentPermissions = store.getters['auth/getPermissions'];
        if (Object.keys(currentPermissions).length === 0) {
            try {
                await store.dispatch('auth/permissions');
            } catch (error) {
                console.error('Error loading permissions:', error);
            }
        }
        
        const userRoles = user?.roles?.map(role => role.name) || [];
        const isScreenViewUser = userRoles.includes('screen_view');

        if (isScreenViewUser && !isRfidLogin && to.name === 'RFIDScan') {
            return next(); 
        } else if (isScreenViewUser && !isRfidLogin && to.name === 'home') {
            return next({ name: 'RFIDScan' });
        } else if (isScreenViewUser && !isRfidLogin && to.name !== 'RFIDScan') {
            return next({ name: 'RFIDScan' });
        } else if (isScreenViewUser && isRfidLogin && to.name === 'RFIDScan') {
            return next({ name: 'home' }); 
        } else if (!isScreenViewUser && to.name === 'RFIDScan') {
            return next({ name: 'home' });
        }

         if (to.meta.requiresGuest) {
            if (isRfidLogin && to.name === 'SignIn') {
                return next({ name: 'RFIDScan' });
            } else {
                return next({ name: 'home' });
            }
        }

    } else { // User is NOT authenticated
        if (to.name === 'RFIDScan') {
            return next({ name: 'SignIn' });
        } 
        else if (to.name === 'home') {
            return next({ name: 'RFIDScan' }); 
        } 
        else if (to.meta.requiresAuth) { 
            return next({ name: 'SignIn' });
        }
    }

    next(); 
});

export default router
